/**
 * Inspector field visibility — declarative map that gates which inspector
 * controls appear for each section-type + layout combination.
 *
 * When you add a section, add an entry (use `{ showAll: true }` if it needs
 * no per-layout gating) — `VISIBILITY_MAP satisfies Record<SectionType, …>`
 * fails `type-check` until you do.
 *
 * When you add/rename a layout to a gated section's Zod enum, add it to that
 * section's `layouts` — the strict `gated<Layout>(…)` parameter fails
 * `type-check` until you do.
 *
 * Hiding a control does NOT clear the stored prop value — if a user sets an
 * image on a split Hero then switches to centered, the value is simply not
 * shown; switching back reveals it intact.
 */
import type { Props as HeroProps } from "@sections/Hero";
import type { Props as PricingTableProps } from "@sections/PricingTable";
import type { Props as BlogGridProps } from "@sections/BlogGrid";
import type { Props as TextBlockProps } from "@sections/TextBlock";
import type { Props as TestimonialsProps } from "@sections/Testimonials";
import type { Props as CTABannerProps } from "@sections/CTABanner";
import type { SectionType } from "@sections/registry";

/* ── Control key union ────────────────────────────────────────── */

/** Every control key the visibility map governs. */
type ControlKey =
  | "backgroundStyle"
  | "density"
  | "width"
  | "textWidth"
  | "headingLevel"
  | "alignment"
  | "cardStyle"
  | "ctaStyle"
  | "ctaColor"
  | "imageShape"
  | "overlay"
  | "imageUrl"
  | "backgroundImageUrl"
  | "imageAlt"
  | "ctaHref"
  | "showDate"
  | "showAuthor"
  | "readMoreLabel"
  | "imageAspect"
  | "highlightIndex"
  | "showCta"
  | "dividerStyle";

/* ── Layout-type extraction ───────────────────────────────────── */

/**
 * Compile-time drift guard: layout types are derived from each section's
 * Props type (which is `z.infer<typeof schema>`). Adding a new layout
 * value to the Zod enum without updating the map below causes a compile
 * error because `layouts` must be a complete `Record<Layout, ...>`.
 */
type HeroLayout = HeroProps["layout"];
type PricingTableLayout = PricingTableProps["layout"];
type BlogGridLayout = BlogGridProps["layout"];
type TextBlockLayout = TextBlockProps["layout"];
type TestimonialsLayout = TestimonialsProps["layout"];
type CTABannerLayout = CTABannerProps["layout"];

/* ── Visibility map shape ─────────────────────────────────────── */

type SectionVisibility =
  | { showAll: true }
  | { common?: ControlKey[]; layouts: Record<string, ControlKey[]> };

/* ── Gated helper ────────────────────────────────────────────── */

/**
 * Build a gated visibility entry. The `layouts` parameter is strictly typed
 * to `Record<L, ControlKey[]>`, so a missing layout is a compile error.
 * The return type is the loose `SectionVisibility` union member so it fits
 * the master map.
 */
function gated<L extends string>(
  layouts: Record<L, ControlKey[]>,
  common: ControlKey[] = [],
): SectionVisibility {
  return { common, layouts };
}

/* ── Per-section maps ─────────────────────────────────────────── */

// `textWidth` constrains the subheading measure; `banner` renders no
// subheading at all (see BannerLayout), so it is deliberately absent there.
// Every Hero layout except `banner` renders through a non-bare SectionShell
// (or, for `image`, paints the background itself), so all of them honor a
// stored backgroundImageUrl + overlay. Banner bypasses SectionShell entirely
// and ignores those props. Overlay is also gated in InspectorPanel to only
// appear once an image is set.
const heroVisibility = gated<HeroLayout>(
  {
    centered: ["density", "width", "textWidth", "headingLevel", "ctaStyle", "ctaColor", "ctaHref", "backgroundImageUrl", "overlay"],
    left: ["density", "width", "textWidth", "headingLevel", "ctaStyle", "ctaColor", "ctaHref", "backgroundImageUrl", "overlay"],
    split: ["density", "width", "textWidth", "headingLevel", "ctaStyle", "ctaColor", "ctaHref", "imageUrl", "imageShape", "imageAlt", "backgroundImageUrl", "overlay"],
    image: ["density", "textWidth", "headingLevel", "ctaStyle", "ctaColor", "ctaHref", "alignment", "backgroundImageUrl", "overlay"],
    banner: ["ctaHref"],
  },
  ["backgroundStyle"],
);

const pricingTableVisibility = gated<PricingTableLayout>(
  {
    panels: ["ctaColor", "showCta", "ctaHref", "highlightIndex"],
    "rate-table": [],
  },
  ["backgroundStyle", "density", "width", "headingLevel"],
);

const blogGridVisibility = gated<BlogGridLayout>(
  {
    grid: ["showDate", "showAuthor", "readMoreLabel", "imageAspect"],
    featured: [],
  },
  ["backgroundStyle", "density", "width", "headingLevel"],
);

const textBlockVisibility = gated<TextBlockLayout>(
  {
    prose: ["density", "alignment"],
    "heading-text": ["density", "alignment", "headingLevel"],
    "two-column": ["density", "alignment", "headingLevel"],
    "pull-quote": ["density", "alignment"],
    divider: ["dividerStyle"],
  },
  ["backgroundStyle", "width"],
);

const testimonialsVisibility = gated<TestimonialsLayout>(
  {
    single: [],
    grid: ["cardStyle"],
    carousel: ["cardStyle"],
  },
  ["backgroundStyle", "density", "width", "headingLevel"],
);

// All three CTABanner layouts honor globals.width: band/boxed via a bare
// SectionShell + BAND_WIDTH, split via a non-bare SectionShell container.
const ctaBannerVisibility = gated<CTABannerLayout>(
  {
    band: [],
    split: [],
    boxed: [],
  },
  ["backgroundStyle", "density", "headingLevel", "ctaColor", "ctaHref", "width"],
);

/* ── Master registry ──────────────────────────────────────────── */

const VISIBILITY_MAP = {
  Hero: heroVisibility,
  PricingTable: pricingTableVisibility,
  BlogGrid: blogGridVisibility,
  BlogList: { showAll: true },
  TextBlock: textBlockVisibility,
  Testimonials: testimonialsVisibility,
  CTABanner: ctaBannerVisibility,
  FeatureGrid: { showAll: true },
  FAQ: { showAll: true },
  CustomEmbed: { showAll: true },
  ImageText: { showAll: true },
  StatsBar: { showAll: true },
  BookingWidget: { showAll: true },
  ProcessSteps: { showAll: true },
  ContactCard: { showAll: true },
  TeamGrid: { showAll: true },
  FeatureShowcase: { showAll: true },
  FeatureList: { showAll: true },
  CTACards: { showAll: true },
  ComparisonTable: { showAll: true },
  LogoStrip: { showAll: true },
  VideoEmbed: { showAll: true },
  MapEmbed: { showAll: true },
  LeadForm: { showAll: true },
} satisfies Record<SectionType, SectionVisibility>;

/* ── Public API ───────────────────────────────────────────────── */

/**
 * Returns true if `control` should be shown for the given section type and
 * current layout value.  Unmapped sections always return true.
 */
export function isControlVisible(
  sectionType: string,
  currentLayout: string,
  control: ControlKey,
): boolean {
  const entry = VISIBILITY_MAP[sectionType as SectionType];
  if (!entry) return true;
  if ("showAll" in entry) return true;

  const commonSet = entry.common ?? [];
  const layoutSet: ControlKey[] | undefined = entry.layouts[currentLayout];

  if (layoutSet === undefined) return true;

  return commonSet.includes(control) || layoutSet.includes(control);
}
